/**
 * Coursera toolbar eligibility: a localStorage flag set while staff is logged in so the toolbar
 * can still load after logout on the same browser. `getToolbarSessionState()` is the single read path
 * for lazy mount, `canRender`, and panels.
 */
import { isCourserian } from 'js/lib/user';

import localStorageEx from 'bundles/common/utils/localStorageEx';
import { isLocalDev } from 'bundles/coursera-toolbar/utils/environmentUtils';

/** localStorage key for “Courserian has used this browser” */
export const COURSERIAN_TOOLBAR_LOCAL_STORAGE_KEY = 'courseraToolbar.visible';

const LS_VALUE = '1';

/** True when the persisted eligibility flag is set (via {@link persistCourserianToolbarFlag} while logged in as Courserian). */
export function hasStoredCourserianSession(): boolean {
  const value = localStorageEx.getItem(COURSERIAN_TOOLBAR_LOCAL_STORAGE_KEY, (item) => item, '', '');
  return value === LS_VALUE;
}

/** Call while Courserian is logged in so {@link hasStoredCourserianSession} is true after logout. */
export function persistCourserianToolbarFlag(): void {
  localStorageEx.setItem(COURSERIAN_TOOLBAR_LOCAL_STORAGE_KEY, LS_VALUE, String);
}

/** Snapshot for mount checks, `canRender`, and panels (one read of env + storage). */
export type ToolbarSessionState = {
  isCourserianLoggedIn: boolean;
  hasStoredCourserianSession: boolean;
  isLocalDev: boolean;
};

export function getToolbarSessionState(): ToolbarSessionState {
  return {
    isCourserianLoggedIn: isCourserian(),
    hasStoredCourserianSession: hasStoredCourserianSession(),
    isLocalDev: isLocalDev(),
  };
}

/**
 * Same rule as lazy toolbar mount: local dev, logged-in Courserian, or logged-out with persisted flag.
 * Use as `Plugin.canRender` when a plugin should appear for every allowed toolbar viewer.
 */
export function matchesToolbarAudience(state: ToolbarSessionState): boolean {
  return state.isLocalDev || state.isCourserianLoggedIn || state.hasStoredCourserianSession;
}

/** Whether `LazyCourseraToolbar` should load. */
export function shouldMountToolbar(): boolean {
  return matchesToolbarAudience(getToolbarSessionState());
}
